/**
 * Video Editor Configuration
 * 
 * Defines which controls are visible for each module type.
 * This allows the same editor to be used across YouTube, Reels, and Talking Videos.
 */

// Aspect ratio options
export const ASPECT_RATIOS = [
  { id: '16:9', label: '16:9', desc: 'Landscape (YouTube)', width: 1920, height: 1080 },
  { id: '9:16', label: '9:16', desc: 'Vertical (Reels/TikTok)', width: 1080, height: 1920 },
  { id: '1:1', label: '1:1', desc: 'Square (Instagram)', width: 1080, height: 1080 },
  { id: '4:5', label: '4:5', desc: 'Portrait (Instagram)', width: 1080, height: 1350 },
];

// Caption style presets
export const CAPTION_STYLES = [
  { id: 'minimal', label: 'Minimal', desc: 'Texto limpio pequeño', fontWeight: 400, fontSize: '14px', color: '#ffffff', bg: 'transparent', textShadow: 'none', padding: '2px 6px' },
  { id: 'bold', label: 'Bold', desc: 'Texto grande amarillo', fontWeight: 800, fontSize: '22px', color: '#ffff00', bg: 'rgba(0,0,0,0.6)', textShadow: '0 2px 4px rgba(0,0,0,0.8)', padding: '4px 10px' },
  { id: 'karaoke', label: 'Karaoke', desc: 'Resalte palabra a palabra', fontWeight: 700, fontSize: '18px', color: '#ffffff', bg: 'hsl(var(--primary))', textShadow: 'none', padding: '3px 8px' },
  { id: 'boxed', label: 'Boxed', desc: 'Caja de fondo oscuro', fontWeight: 600, fontSize: '16px', color: '#ffffff', bg: 'rgba(0,0,0,0.85)', textShadow: 'none', padding: '6px 14px' },
  { id: 'outline', label: 'Outline', desc: 'Texto con borde', fontWeight: 700, fontSize: '20px', color: '#ffffff', bg: 'transparent', textShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000', padding: '4px 8px' },
];

// Caption languages
export const CAPTION_LANGUAGES = [
  { id: 'auto', label: 'Auto-detect' },
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Spanish' },
  { id: 'pt', label: 'Portuguese' },
  { id: 'fr', label: 'French' },
  { id: 'de', label: 'German' },
  { id: 'it', label: 'Italian' },
  { id: 'ja', label: 'Japanese' },
  { id: 'ko', label: 'Korean' },
  { id: 'zh', label: 'Chinese (Simplified)' },
  { id: 'zh-tw', label: 'Chinese (Traditional)' },
  { id: 'ar', label: 'Arabic' },
  { id: 'ru', label: 'Russian' },
  { id: 'hi', label: 'Hindi' },
  { id: 'tr', label: 'Turkish' },
  { id: 'nl', label: 'Dutch' },
  { id: 'pl', label: 'Polish' },
  { id: 'sv', label: 'Swedish' },
  { id: 'id', label: 'Indonesian' },
  { id: 'vi', label: 'Vietnamese' },
  { id: 'th', label: 'Thai' },
  { id: 'ro', label: 'Romanian' },
  { id: 'uk', label: 'Ukrainian' },
];

// Caption font options (viral/creator fonts using web-safe stacks)
export const CAPTION_FONTS = [
  { id: 'default', label: 'Default', family: 'system-ui, -apple-system, sans-serif' },
  { id: 'impact', label: 'Impact', family: '"Impact", "Arial Black", sans-serif' },
  { id: 'arial-bold', label: 'Arial Bold', family: '"Arial", "Helvetica Neue", sans-serif' },
  { id: 'georgia', label: 'Georgia', family: '"Georgia", "Times New Roman", serif' },
  { id: 'courier', label: 'Mono/Code', family: '"Courier New", "Lucida Console", monospace' },
];
export const BACKGROUND_TYPES = [
  { id: 'blur', label: 'Blur', desc: 'Blurred video background' },
  { id: 'gameplay', label: 'Gameplay', desc: 'Gaming footage overlay' },
  { id: 'gradient', label: 'Gradient', desc: 'Color gradient' },
  { id: 'solid', label: 'Solid Color', desc: 'Single color' },
  { id: 'image', label: 'Image', desc: 'Custom image' },
  { id: 'none', label: 'None', desc: 'Transparent/letterbox' },
];

// Speaker layout options (for talking videos)
export const SPEAKER_LAYOUTS = [
  { id: 'center', label: 'Center', desc: 'Speaker centered' },
  { id: 'left', label: 'Left', desc: 'Speaker on left' },
  { id: 'right', label: 'Right', desc: 'Speaker on right' },
  { id: 'pip', label: 'Picture-in-Picture', desc: 'Small speaker overlay' },
  { id: 'split', label: 'Split Screen', desc: 'Side by side with content' },
];

// Export quality options
export const EXPORT_QUALITIES = [
  { id: '4k', label: '4K', desc: '3840x2160', premium: true },
  { id: '1080p', label: '1080p', desc: '1920x1080', premium: false },
  { id: '720p', label: '720p', desc: '1280x720', premium: false },
  { id: '480p', label: '480p', desc: '854x480 (Preview)', premium: false },
];

// Module-specific field visibility
export const MODULE_FIELDS = {
  youtube: [
    'title', 'thumbnail', 'aspectRatio', 'trim', 'captions', 
    'chapters', 'exportQuality'
  ],
  reels: [
    'title', 'aspectRatio', 'trim', 'hook', 'cta', 'captions', 
    'music', 'zoom', 'background', 'exportQuality'
  ],
  talking: [
    'title', 'aspectRatio', 'trim', 'captions', 'speakerLayout',
    'background', 'branding', 'music', 'exportQuality'
  ],
  // Full editor shows all fields
  full: [
    'title', 'thumbnail', 'aspectRatio', 'trim', 'hook', 'cta',
    'captions', 'chapters', 'music', 'zoom', 'speakerLayout',
    'background', 'branding', 'exportQuality'
  ]
};

// Default video settings
export const getDefaultVideoSettings = () => ({
  // Basic
  title: '',
  thumbnail: null,
  
  // Format
  aspectRatio: '9:16',
  exportQuality: '1080p',
  
  // Timing
  startTime: 0,
  endTime: 0,
  duration: 0,
  
  // Captions
  captionsEnabled: true,
  captionStyle: 'bold',
  captionLanguage: 'auto',
  captionPosition: 'bottom', // top, center, bottom
  captionColor: '#ffffff',
  captionFont: 'default',
  
  // Hook & CTA
  hookText: '',
  hookPosition: 'top',
  hookDuration: 3, // seconds
  ctaText: '',
  ctaPosition: 'bottom',
  ctaDuration: 5,
  
  // Visual
  zoom: 1.0,
  zoomEnabled: false,
  speakerLayout: 'center',
  background: 'solid',
  backgroundColor: '#000000',
  
  // Audio
  musicEnabled: false,
  musicVolume: 50,
  musicTrack: null,
  originalAudioVolume: 100,
  
  // Branding
  brandingEnabled: false,
  watermarkPosition: 'bottom-right',
  watermarkOpacity: 80,
});

// Field labels and descriptions
export const FIELD_CONFIG = {
  title: { label: 'Title', section: 'basic' },
  thumbnail: { label: 'Thumbnail', section: 'basic' },
  aspectRatio: { label: 'Aspect Ratio', section: 'format' },
  exportQuality: { label: 'Export Quality', section: 'format' },
  trim: { label: 'Trim', section: 'timing' },
  captions: { label: 'Captions', section: 'text' },
  chapters: { label: 'Chapters', section: 'text' },
  hook: { label: 'Hook Text', section: 'text' },
  cta: { label: 'Call to Action', section: 'text' },
  music: { label: 'Music', section: 'audio' },
  zoom: { label: 'Zoom / Punch-in', section: 'visual' },
  speakerLayout: { label: 'Speaker Layout', section: 'visual' },
  background: { label: 'Background', section: 'visual' },
  branding: { label: 'Branding', section: 'branding' },
};

export default {
  ASPECT_RATIOS,
  CAPTION_STYLES,
  CAPTION_LANGUAGES,
  BACKGROUND_TYPES,
  SPEAKER_LAYOUTS,
  EXPORT_QUALITIES,
  MODULE_FIELDS,
  FIELD_CONFIG,
  getDefaultVideoSettings,
};
